import compoundedMedsStates from "@compounded-meds-states";
import {
  differenceInMinutes,
  format,
  formatDistanceStrict,
  isBefore,
  parseISO,
} from "date-fns";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";

import {
  CHOCO_CHILL_MINT_FLAVOR,
  CITRUS_ZEN_FLAVOR,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_SCOPOLAMINE_ADDON,
  CONSULTATION_TYPE_INSOMNIA,
  CONSULTATION_TYPE_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
  DB_STATUS_ACTIVE,
  DB_STATUS_PAUSED,
  DOCTOR_VISIT_STATUS_CLOSED_COMPLETE,
  FILL_PHARMACY_ID_HONEYBEE_CA,
  FILL_REQUEST_STATUS_CANCELLED,
  FILL_REQUEST_STATUS_DELETED,
  FILL_REQUEST_STATUS_ERROR,
  FILL_REQUEST_STATUS_FILLED,
  FILL_REQUEST_STATUS_INACTIVE,
  FILL_REQUEST_STATUS_MATCH_ERROR,
  FILL_REQUEST_STATUS_MATCHING_PENDING,
  FILL_REQUEST_STATUS_NEW,
  FILL_REQUEST_STATUS_PAYMENT_PENDING,
  FILL_REQUEST_STATUS_PAYMENT_UNCOLLECTIBLE,
  FILL_REQUEST_STATUS_PROCESSING,
  FILL_REQUEST_STATUS_PROCESSING_DONE,
  FILL_REQUEST_STATUS_SENDING_ERROR,
  FILL_REQUEST_STATUS_SENT,
  FLAVOR_NAME_CHOCO_CHILL_MINT,
  FLAVOR_NAME_CITRUS_ZEN,
  FLAVOR_NAME_FRUIT_PUNCH,
  FLAVOR_NAME_SPEARMINT,
  FLAVOR_NAME_TANGERINE,
  FRUIT_PUNCH_FLAVOR,
  ORDER_TYPE_ATE_SCO_1_PACK,
  ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
  ORDER_TYPE_ATE_SCO_1_PACK_REFILL,
  ORDER_TYPE_ATE_SCO_2_PACK,
  ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP,
  ORDER_TYPE_ATE_SCO_2_PACK_REFILL,
  ORDER_TYPE_PROP_1_PACK,
  ORDER_TYPE_PROP_1_PACK_FLAVORED,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_REFILL,
  ORDER_TYPE_PROP_2_PACK,
  ORDER_TYPE_PROP_2_PACK_FLAVORED,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_REFILL,
  ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS,
  ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
  ORDER_TYPE_PROP_VISIT_ONLY,
  ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP,
  ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER,
  ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER,
  ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL,
  ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH,
  ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_TWO_MONTHS,
  ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH,
  ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_TWO_MONTHS,
  RX_EXPIRATION_DAYS,
  SPEARMINT_FLAVOR,
  TANGERINE_FLAVOR,
  VISIT_TYPE_GENERALIZED_ANXIETY,
  VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP,
  VISIT_TYPE_INSOMNIA,
  VISIT_TYPE_INSOMNIA_FOLLOW_UP,
  VISIT_TYPE_PERFORMANCE_ANXIETY,
  VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
} from "../sharedConstants";
// -------------------------------------------------------------------

import imgChocoChillMint from "~assets/images/flavors/choco-chill.png";
import imgCitrusZen from "~assets/images/flavors/citrus-zen.png";
import imgSamplePackAllFlavors from "~assets/images/flavors/sample-pack-all-flavors.png";
import imgSpearmint from "~assets/images/flavors/spearmint-flavor.png";
import imgTangerine from "~assets/images/flavors/tangerine-flavor.png";
import imgFruitPunch from "~assets/images/flavors/tropical-fruit-medley-flavor.png";
import imgOnePack from "~assets/images/visits/one-pack.png";
import imgSleep from "~assets/images/visits/sleep-bottles.jpg";
import imgTwoPack from "~assets/images/visits/two-pack.png";

export function isDevelopmentEnvironment() {
  return process.env.NODE_ENV === "development";
}

// isPOBox returns true if the address contains a P.O. Box pattern
export function isPOBox(address) {
  const poBoxRegex = /(P\.?O\.?\s*Box|Post\s*Office\s*Box|PO\s*Box)/gi;
  return poBoxRegex.test(address.address1) || poBoxRegex.test(address.address2);
}

// isPharmacyPickupOrderType returns true if the orderType is a pharmacy pickup order type
export function isPharmacyPickupOrderType(orderType) {
  return orderType?.includes("visit-only");
}

export function isMailOrderOrderType(orderType) {
  return !isPharmacyPickupOrderType(orderType);
}

// isVisitOnlyBBOrderType
export function isVisitOnlyBBOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_PROP_VISIT_ONLY ||
    orderType == ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP
  );
}

export function isMailOrderBBOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_PROP_2_PACK ||
    orderType == ORDER_TYPE_PROP_2_PACK_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_2_PACK_REFILL ||
    orderType == ORDER_TYPE_PROP_1_PACK ||
    orderType == ORDER_TYPE_PROP_1_PACK_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_1_PACK_REFILL ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL
  );
}

export function isFlavoredBBOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP ||
    orderType == ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL
  );
}

export function isBBOrderType(orderType) {
  return isVisitOnlyBBOrderType(orderType) || isMailOrderBBOrderType(orderType);
}

export function isScopolamineAddonOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER ||
    orderType == ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER ||
    orderType == ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY
  );
}

export function isAteScoCompoundedOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_ATE_SCO_1_PACK ||
    orderType == ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP ||
    orderType == ORDER_TYPE_ATE_SCO_2_PACK ||
    orderType == ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP
  );
}

export function isPharmacyPickupSleepOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL ||
    orderType == ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH ||
    orderType == ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_TWO_MONTHS ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP
  );
}

export function isMailOrderSleepOrderType(orderType) {
  return (
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL ||
    orderType == ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH ||
    orderType == ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_TWO_MONTHS ||
    orderType == ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP
  );
}

export function isMailOrderType(orderType) {
  return (
    isMailOrderBBOrderType(orderType) || isMailOrderSleepOrderType(orderType)
  );
}

export function isSleepOrderType(orderType) {
  return (
    isPharmacyPickupSleepOrderType(orderType) ||
    isMailOrderSleepOrderType(orderType)
  );
}

// sleepFulfillmentPairs is the single source of truth for pairing each sleep order type's
// mail-order and pharmacy-pickup variants at a given cadence. The string shapes are NOT uniform
// (trials use a different position and singular/plural differ), so do NOT derive these with string
// surgery — keep the table explicit.
const sleepFulfillmentPairs = [
  {
    mail: ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
    pickup: ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
  },
  {
    mail: ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY,
    pickup: ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
  },
  {
    mail: ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL,
    pickup: ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL,
  },
  {
    mail: ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP,
    pickup: ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP,
  },
  {
    mail: ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH,
    pickup: ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH,
  },
  {
    mail: ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_TWO_MONTHS,
    pickup: ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_TWO_MONTHS,
  },
];

// sleepOrderTypeWithFulfillment returns the sleep order type that keeps the SAME cadence as
// `orderType` but uses the requested fulfillment (isPickup === true -> pharmacy-pickup/visit-only
// variant; false -> mail-order variant). When `orderType` is not a recognized sleep type (empty,
// undefined, or a non-sleep slug), it defaults to the monthly membership of the requested
// fulfillment. This is the single place that knows the mail<->pickup pairing, so callers can switch
// fulfillment without losing cadence and without hard-coding variant strings.
export function sleepOrderTypeWithFulfillment(orderType, isPickup) {
  const pair = sleepFulfillmentPairs.find(
    (p) => p.mail === orderType || p.pickup === orderType,
  );
  if (!pair) {
    return isPickup
      ? ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY
      : ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY;
  }
  return isPickup ? pair.pickup : pair.mail;
}

// isPharmacyPickupFromVisit returns true if the differentPharmacyRawCookie is not empty
export function isPharmacyPickupFromVisit(differentPharmacyRawCookie) {
  // console.log("rawCookie: ", differentPharmacyRawCookie);
  // console.log(differentPharmacyRawCookie != `""`);
  return differentPharmacyRawCookie && differentPharmacyRawCookie != `""`;
}

export function isVisitTypeInsomnia(visitType) {
  return (
    visitType == VISIT_TYPE_INSOMNIA ||
    visitType == VISIT_TYPE_INSOMNIA_FOLLOW_UP
  );
}

export function isVisitTypePerformanceAnxiety(visitType) {
  return (
    visitType == VISIT_TYPE_PERFORMANCE_ANXIETY ||
    visitType == VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP
  );
}

export function isVisitTypeGeneralizedAnxiety(visitType) {
  return (
    visitType == VISIT_TYPE_GENERALIZED_ANXIETY ||
    visitType == VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP
  );
}

export function isConsultationTypePerformanceAnxietyInitial(consultationType) {
  return (
    consultationType == CONSULTATION_TYPE_PERFORMANCE_ANXIETY ||
    consultationType == CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY
  );
}
export function isConsultationTypePerformanceAnxietyFollowUp(consultationType) {
  return (
    consultationType == CONSULTATION_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP ||
    consultationType ==
      CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP
  );
}
export function isConsultationTypeInsomniaInitial(consultationType) {
  return (
    consultationType == CONSULTATION_TYPE_INSOMNIA ||
    consultationType == CONSULTATION_TYPE_ABBREVIATION_INSOMNIA
  );
}

export function isConsultationTypeInsomniaFollowUp(consultationType) {
  return [
    CONSULTATION_TYPE_INSOMNIA_FOLLOW_UP,
    CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  ].includes(consultationType?.toLowerCase());
}

export function isConsultationTypeInsomnia(consultationType) {
  return (
    isConsultationTypeInsomniaInitial(consultationType) ||
    isConsultationTypeInsomniaFollowUp(consultationType)
  );
}

export function isConsultationTypeGeneralizedAnxiety(consultationType) {
  return [
    CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY,
    CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  ].includes(consultationType?.toLowerCase());
}

// isPickupAvailableForConsultationType reports whether a visit of this consultation type may be
// fulfilled by local pharmacy pickup (a "visit-only" order type). Generalized-anxiety visits
// (gac/gaf) are mail-order ONLY: their compounded atenolol+scopolamine products can only be filled
// by the mail-order compounding pharmacy, and no visit-only SKU exists for them. Mirrors
// DoctorVisitTypeIsMailOrderOnly on the Go side (app/domain_types.go) — keep the two in sync.
export function isPickupAvailableForConsultationType(consultationType) {
  return !isConsultationTypeGeneralizedAnxiety(consultationType);
}

// -------------------------------------------------------------------

export function refillOrderTypeFor(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return ORDER_TYPE_PROP_2_PACK_REFILL;
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return ORDER_TYPE_PROP_1_PACK_REFILL;
    case ORDER_TYPE_PROP_VISIT_ONLY:
      return ORDER_TYPE_PROP_VISIT_ONLY;
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
      return ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL;
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL;
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED;
    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
      return ORDER_TYPE_ATE_SCO_1_PACK_REFILL;
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return ORDER_TYPE_ATE_SCO_2_PACK_REFILL;
  }
  return "";
}

export function followUpOrderTypeForRefillType(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK_REFILL:
      return ORDER_TYPE_PROP_2_PACK_FOLLOW_UP;
    case ORDER_TYPE_PROP_1_PACK_REFILL:
      return ORDER_TYPE_PROP_1_PACK_FOLLOW_UP;
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL:
      return ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP;
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL:
      return ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP;
    default:
      return orderType;
  }
}

// isRefillOrderType reports whether an order type is a dashboard-refill SKU. Refill checkout
// sessions reference the patient's ORIGINAL (closed) visit, so flows that would re-complete a
// visit — like switching fulfillment to local pickup, which has no refill SKU and must instead go
// through a follow-up visit — need to detect them explicitly. Mirrors KickOrderTypeIsRefill on the
// Go side (app/service/service_utils.go) — keep the two in sync.
export function isRefillOrderType(orderType) {
  return [
    ORDER_TYPE_PROP_1_PACK_REFILL,
    ORDER_TYPE_PROP_2_PACK_REFILL,
    ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
    ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
    ORDER_TYPE_ATE_SCO_1_PACK_REFILL,
    ORDER_TYPE_ATE_SCO_2_PACK_REFILL,
  ].includes(orderType);
}

// isOriginalOrFollowUpVisit returns true if the orderType on a doctorVisit is not a refill order
export function isOriginalOrFollowUpVisit(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return true;
  }
  return false;
}

export function getDrugQuantityForOrderType(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_REFILL:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return 48;

    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_REFILL:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
      return 24;

    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER:
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY:
      return 4;
    case ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER:
      return 10;

    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return 6;

    case ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS:
      return 4;
  }
}

export function getNumRefillsForOrderType(orderType) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER:
    case ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER:
    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return 3;

    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return 1;
  }

  return 1;
}

// isDoctorVisitComplete returns true if a doctor visit has status of Closed Complete ('CC')
export function isDoctorVisitComplete(status) {
  return status == DOCTOR_VISIT_STATUS_CLOSED_COMPLETE;
}

// hasRefillsRemaining returns true if a doctorPrescription has refills available
export function hasRefillsAvailable(refills, numRefillsFilled) {
  return refills - numRefillsFilled > 0;
}

export function numRefillsRemaining(refills, numRefillsFilled) {
  let numRemaining = refills - numRefillsFilled;
  return numRemaining < 0 ? 0 : numRemaining;
}

// displayFriendlyLongDate returns date in "October 15 2020" format
export function displayFriendlyLongDate(date) {
  return format(new Date(date), "MMMM dd yyyy");
}

export function displayFriendlyDate(date) {
  return format(new Date(date), "MMM d, yyyy");
}

// displayISOShortDate returns date in "1/2/21" format, and returns blank if date is empty
export function displayISOShortDate(date) {
  const shortDate = parseISO(date);

  return isBefore(shortDate, new Date(0, 0, 1))
    ? ""
    : format(shortDate, "M/d/yy");
}

// displayFriendlyPrescriptionExpirationLongDate adds RX_EXPIRATION_DAYS to the original
// prescription visit date.
export function displayFriendlyPrescriptionExpirationLongDate(date) {
  var result = new Date(date);
  result.setDate(result.getDate() + RX_EXPIRATION_DAYS);

  return format(result, "MMMM dd yyyy");
}

export function displayFriendlyShortenedOrderType(orderType, subcategory) {
  switch (orderType) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return "2 Pack";
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return "1 Pack";
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return "Pharmacy Pick-Up";
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return `2 Pack (${getFlavorName(subcategory) || "Flavored"})`;
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
      return `1 Pack (${getFlavorName(subcategory) || "Flavored"})`;
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return `Sample Pack (${getFlavorName(subcategory) || "Flavored"})`;
    case ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS:
      return "All Flavors Sample Pack";
  }
  return "";
}

export function getOrderTypeImageSrc(productSlug, subcategory) {
  switch (productSlug) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_REFILL:
      return imgTwoPack;

    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_REFILL:
    case ORDER_TYPE_PROP_VISIT_ONLY:
      return imgOnePack;

    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL:
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP:
      return imgSleep;

    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP: {
      switch (subcategory) {
        case TANGERINE_FLAVOR:
          return imgTangerine;
        case SPEARMINT_FLAVOR:
          return imgSpearmint;
        case FRUIT_PUNCH_FLAVOR:
          return imgFruitPunch;
        case CHOCO_CHILL_MINT_FLAVOR:
          return imgChocoChillMint;
        case CITRUS_ZEN_FLAVOR:
          return imgCitrusZen;
        default:
          return imgSpearmint;
      }
    }

    case ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS:
      return imgSamplePackAllFlavors;

    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_REFILL:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_REFILL:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP: {
      switch (subcategory) {
        case CHOCO_CHILL_MINT_FLAVOR:
          return imgChocoChillMint;
        case CITRUS_ZEN_FLAVOR:
          return imgCitrusZen;
        default:
          return imgOnePack;
      }
    }
  }

  return imgOnePack;
}

// Per-product nudges layered on top of the portal's fixed-size thumbnail box.
// Some product renders sit off-center within their image canvas and need a small
// shift/scale to look centered. Returns {} when no adjustment is needed.
export function getOrderTypeImageStyle(productSlug) {
  switch (productSlug) {
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_REFILL:
      return { transform: "translateX(4px) scale(1.2)" };
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_REFILL:
    case ORDER_TYPE_PROP_VISIT_ONLY:
      return { transform: "scale(1.2)" };
    default:
      return {};
  }
}

export function getVisitTypeImageSrc(visitType) {
  switch (visitType) {
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      return imgOnePack;

    case VISIT_TYPE_INSOMNIA:
    case VISIT_TYPE_INSOMNIA_FOLLOW_UP:
      return imgSleep;
  }
  return imgOnePack;
}

export function getConsultationNameFromVisitType(visitType) {
  switch (visitType) {
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      return "Performance Anxiety";

    case VISIT_TYPE_INSOMNIA:
    case VISIT_TYPE_INSOMNIA_FOLLOW_UP:
      return "Sleep";

    case VISIT_TYPE_GENERALIZED_ANXIETY:
    case VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP:
      return "Generalized Anxiety";
  }
}

export function getConsultationNameFromType(consultationType) {
  switch (consultationType) {
    // Performance Anxiety
    case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY:
      return "Performance Anxiety";
    case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP:
      return "Performance Anxiety Follow-Up";
    case CONSULTATION_TYPE_ABBREVIATION_SCOPOLAMINE_ADDON:
      return "Scopolamine Addon";
    // Insomnia
    case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA:
      return "Insomnia";
    case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP:
      return "Insomnia Follow-Up";
    case CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY:
      return "Generalized Anxiety";
    case CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP:
      return "Generalized Anxiety Follow-Up";
  }
}

export function latestVisitCompletedAtOrVisitStartedAt(visitDate, completedAt) {
  return completedAt > visitDate ? completedAt : visitDate;
}

export function mapFillRequestStatusToUserFriendlyStatus(status) {
  switch (status) {
    case FILL_REQUEST_STATUS_NEW:
      return "New";
    case FILL_REQUEST_STATUS_SENT:
      return "Sent";
    case FILL_REQUEST_STATUS_SENDING_ERROR:
      return "Send Error";
    case FILL_REQUEST_STATUS_FILLED:
      return "Filled";
    case FILL_REQUEST_STATUS_ERROR:
      return "Error";
    case FILL_REQUEST_STATUS_CANCELLED:
      return "Cancelled";
    case FILL_REQUEST_STATUS_PROCESSING:
      return "Processing";
    case FILL_REQUEST_STATUS_PROCESSING_DONE:
      return "Processing Done";
    case FILL_REQUEST_STATUS_DELETED:
      return "Deleted";
    case FILL_REQUEST_STATUS_INACTIVE:
      return "Inactive";
    case FILL_REQUEST_STATUS_MATCHING_PENDING:
      return "Pending RX";
    case FILL_REQUEST_STATUS_PAYMENT_PENDING:
      return "Pending Payment";
    case FILL_REQUEST_STATUS_MATCH_ERROR:
      return "RX Match Error";
    case FILL_REQUEST_STATUS_PAYMENT_UNCOLLECTIBLE:
      return "Payment Uncollectible";
  }

  return status;
}

export async function copyTextToClipboard(copyText) {
  navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
    console.log("clipboard access state: ", result.state);
    if (result.state == "granted" || result.state == "prompt") {
      try {
        navigator.clipboard.writeText(copyText);
        console.log("Copied to clipboard: ", copyText);
        /* Resolved - text copied to clipboard successfully */
      } catch (err) {
        console.error("Failed to copy: ", err);
        /* Rejected - text failed to copy to the clipboard */
      }
    } else {
      alert(`Clipboard access denied: ${copyText}`);
    }
  });
}

export const getGoogleAPIKey = () => {
  return (
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "AIzaSyB-Lywz9aSHxSHQF23lrJJMKG3I75H2DmE"
  );
};

export const getZerobounceAPIKey = () => {
  return (
    process.env.REACT_APP_ZEROBOUNCE_API_KEY ||
    "84ed5171801b4eb7b70fc3a41f2bbe33"
  );
};

export function formatPhoneNumber(phoneNumberString) {
  var cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return "(" + match[1] + ") " + match[2] + "-" + match[3];
  }
  return null;
}

export function useQueryParams() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export const supportedUSStates = [
  "AL",
  "CA",
  "CO",
  "CT",
  "FL",
  "GA",
  "HI",
  "IA",
  "IL",
  "KS",
  "KY",
  "MA",
  "MD",
  "ME",
  "MI",
  "MN",
  "MT",
  "NC",
  "ND",
  "NE",
  "NH",
  "NJ",
  "NY",
  "OH",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VA",
  "WA",
  "WI",
  "WY",
];

/**
 * Validates that a visit's destination state is prescribable.
 * For mail-order: checks shipping address state.
 * For pharmacy pickup: skips check (pharmacy state is validated separately).
 * Returns an error message string if invalid, or null if valid.
 */
export function validateVisitStatePrescribable(visit) {
  if (isPharmacyPickupOrderType(visit.orderType)) {
    if (!supportedUSStates.includes(visit.pharmacyInfo.pharmacyState)) {
      return `Cannot send prescriptions to this pharmacy in ${visit.pharmacyInfo.pharmacyState}. Please update the pharmacy to a supported state.`;
    }
    return null;
  }
  if (!supportedUSStates.includes(visit.usState)) {
    return `Cannot ship prescriptions to ${visit.usState}. Please update the shipping address to a supported state.`;
  }
  return null;
}

// SINGLE SOURCE OF TRUTH: the active list lives in app/config/compounded_meds_states.json,
// imported here via the @compounded-meds-states webpack alias AND embedded into Go
// (app/domain_compound_medications.go). Edit the JSON to change it everywhere at once.
//
// States intentionally EXCLUDED (and why) — kept here since JSON can't hold comments;
// update this whenever the JSON changes:
//   AL (HNB not active); AK, AR, AZ, MO, NM, OK, VT, WV (kick not active);
//   IN, LA, MS (kick not active AND HNB not active); DC.
export const supportedUSStatesForCompoundedMeds = compoundedMedsStates;

export const controlledDrugsByState = {
  gabapentin: ["AL", "KY", "MT", "ND", "TN", "VA", "UT", "WV"],
  cyclobenzaprine: ["MN"],
};

export const pdmpDrugsByState = {
  gabapentin: [
    "CT",
    "DC",
    "IN",
    "KS",
    "MA",
    "MN",
    "NC",
    "NE",
    "NJ",
    "OH",
    "OR",
    "WI",
    "WY",
  ],
  cyclobenzaprine: ["DC", "WY"],
};

export const isPDMPSleepMedication = (usState, medicationName) => {
  return (pdmpDrugsByState[medicationName] || []).includes(usState);
};

export const isStateControlledMedication = (usState, medicationName) => {
  return (controlledDrugsByState[medicationName] || []).includes(usState);
};

export const nullUUID = "00000000-0000-0000-0000-000000000000";

export const isNullUUID = (uuid) => {
  return uuid === nullUUID;
};

export const isNullDate = (date) => {
  return (
    date === null ||
    date === undefined ||
    date === "" ||
    date === "0001-01-01T00:00:00Z"
  );
};

// Go's zero time (0001-01-01T00:00:00Z) expressed as unix SECONDS — what the jsonapi
// serializer emits for an unset time.Time.
const GO_ZERO_TIME_UNIX_SECONDS = -62135596800;

// parseApiDate parses a date attribute from any of our APIs into a Date, or null when the value
// is unset or unparseable. The portal GraphQL API serializes times as ISO-8601 strings, but the
// visits jsonapi serializes them as unix SECONDS — `new Date(seconds)` would read the number as
// MILLISECONDS and silently produce a date near 1970, so numeric values (and all-digit strings)
// with magnitude below 1e12 are treated as unix seconds and scaled. Go's zero time — the
// "0001-01-01T00:00:00Z" string or its unix-seconds epoch — means "unset" and parses to null.
export const parseApiDate = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  let numeric = null;
  if (typeof value === "number") {
    numeric = value;
  } else if (typeof value === "string" && /^-?\d+$/.test(value.trim())) {
    numeric = Number(value.trim());
  }
  if (numeric !== null) {
    if (numeric === GO_ZERO_TIME_UNIX_SECONDS) {
      return null;
    }
    // Anything under 1e12 can't be a plausible millisecond timestamp for our data
    // (that's before ~2001), so treat it as unix seconds.
    const millis = Math.abs(numeric) < 1e12 ? numeric * 1000 : numeric;
    const parsed = new Date(millis);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  if (isNullDate(value)) {
    return null;
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
};

// isVisitSnoozed returns true when the visit's snoozedUntil is set (not Go zero time) and in the
// future. A snoozed visit renders a snooze chip alongside its status chip in the EMR and is hidden
// from the doctor queue server-side.
export const isVisitSnoozed = (visit) => {
  if (!visit) {
    return false;
  }
  const until = parseApiDate(visit.snoozedUntil);
  return until !== null && until.getTime() > Date.now();
};

// formatSnoozeRemaining renders how much of a snooze is left, for the value half of the EMR's
// snooze chip. Every surface (visit panel, visits list) formats through this so they can never
// disagree about how long a visit has been put to sleep.
//
// Deliberately duration-only (no wall-clock time, no timezone) — the absolute "Snoozed until …"
// timestamp lives in the chip's tooltip, where there's room for it. Rounds DOWN at every step so
// "7d left" never over-promises; a snooze with 7d23h left reads "7d left", not "8d".
//
// Returns "" when the visit isn't snoozed, so callers can treat empty as "render nothing".
export const formatSnoozeRemaining = (
  snoozedUntil,
  { compact = false, now = Date.now() } = {},
) => {
  const until = parseApiDate(snoozedUntil);
  if (until === null) {
    return "";
  }
  const minutes = differenceInMinutes(until, now);
  if (minutes < 0) {
    return "";
  }
  // Under a minute the countdown is meaningless — the unsnooze is effectively happening.
  if (minutes < 1) {
    return compact ? "waking" : "waking up";
  }

  // Pick the unit ourselves rather than letting date-fns choose: its own unit selection switches to
  // days at the 24h mark, and "1 day left" hides whether the visit wakes tonight or tomorrow
  // evening. Hours run all the way to 48 instead, where "30 hours left" says it plainly.
  // roundingMethod "floor" means "7 days left" never over-promises on a snooze with 7d23h to go.
  const unit = minutes < 60 ? "minute" : minutes < 60 * 48 ? "hour" : "day";
  const distance = formatDistanceStrict(until, now, {
    unit,
    roundingMethod: "floor",
  });
  return compact ? distance : `${distance} left`;
};

// hasUnhandledPatientReply returns true when the patient replied while the visit was Awaiting
// Patient Reply and no EMR user has dispositioned it since (patientRepliedAt set, i.e. not Go zero
// time). The server stamps it from BOTH reply paths (care and doctor messages) and clears it on any
// disposition.
//
// Unlike isVisitSnoozed there's no "in the future" test — this is a latch, not a deadline. Any
// non-zero value means the reply is still outstanding, however long ago it landed; age is what
// makes it urgent, not what makes it expire.
export const hasUnhandledPatientReply = (visit) => {
  if (!visit) {
    return false;
  }
  return parseApiDate(visit.patientRepliedAt) !== null;
};

// formatPatientReplyAge renders how long a patient's reply has been sitting unhandled, for the value
// half of the EMR's reply chip. Mirrors formatSnoozeRemaining's shaping (same unit thresholds, same
// floor rounding) so the two chips can't disagree about how they express a duration.
//
// Returns "" when there's no unhandled reply, so callers can treat empty as "render nothing".
export const formatPatientReplyAge = (
  patientRepliedAt,
  { compact = false, now = Date.now() } = {},
) => {
  const at = parseApiDate(patientRepliedAt);
  if (at === null) {
    return "";
  }
  const minutes = differenceInMinutes(now, at);
  // A reply stamped a moment ago (or a clock skew putting it slightly ahead) reads as "just now"
  // rather than a bare "0 minutes".
  if (minutes < 1) {
    return compact ? "just now" : "just now";
  }
  const unit = minutes < 60 ? "minute" : minutes < 60 * 48 ? "hour" : "day";
  const distance = formatDistanceStrict(at, now, {
    unit,
    roundingMethod: "floor",
  });
  return compact ? distance : `${distance} ago`;
};

// subscriptionGrantsFollowUpAccess returns true when a subscription currently lets the patient do
// a follow-up without reactivating billing: it is active, OR paused but still inside its frozen
// paid-through window (a "scheduled" pause — billing hasn't actually stopped yet). Mirrors the
// server-side ByUserIDAndSubTypeWithFollowUpAccess gate. A paused subscription with no
// paidThroughAt (e.g. paused from the Stripe dashboard, which writes no pause metadata) does NOT
// grant access. Works on both the portal GraphQL shape (ISO-8601 strings) and the visits-API
// jsonapi attributes shape (same field names but unix-seconds times) — see parseApiDate.
export const subscriptionGrantsFollowUpAccess = (
  subscription,
  now = Date.now(),
) => {
  if (!subscription) {
    return false;
  }
  if (subscription.status === DB_STATUS_ACTIVE) {
    return true;
  }
  if (subscription.status !== DB_STATUS_PAUSED) {
    return false;
  }
  const paidThroughAt = parseApiDate(subscription.paidThroughAt);
  return paidThroughAt !== null && paidThroughAt > now;
};

// subscriptionPauseState splits a paused ("PA") subscription into its two functional states.
// Local status flips to PA the moment a pause is requested, but the patient stays in their
// already-paid period until paidThroughAt — billing/shipments only actually stop then.
//   - "scheduled": pause requested but still inside the paid period (paidThroughAt in the
//     future). Functionally the subscription behaves as active.
//   - "paused": the paid period has lapsed (or there's no pause metadata at all, e.g. a pause
//     made from the Stripe dashboard) — resuming requires a restart charge.
// Returns null for any non-paused status. Shared by the patient portal (treatment card chip)
// and the EMR (patient side panel), which must agree on what "paused" means.
export const subscriptionPauseState = (subscription, now = new Date()) => {
  if (!subscription || subscription.status !== DB_STATUS_PAUSED) {
    return null;
  }
  return subscriptionGrantsFollowUpAccess(subscription, now)
    ? "scheduled"
    : "paused";
};

// subscriptionStatusRank orders subscription statuses by how "live" they are, for picking which
// row to surface when a patient has multiple subscription rows of the same subType (the feeding
// query has no ORDER BY, so row order is nondeterministic): active beats paused beats everything
// else (canceled etc.).
export const subscriptionStatusRank = (sub) =>
  sub?.status === DB_STATUS_ACTIVE
    ? 2
    : sub?.status === DB_STATUS_PAUSED
      ? 1
      : 0;

// preferLiveSubscription keeps the more-live of two subscription rows (active > paused > other),
// keeping the incumbent on ties so the choice is stable regardless of row order. Either side may
// be null/undefined (e.g. as a reduce seed), in which case the other is returned — an active row
// can never be displaced by a paused or canceled one.
export const preferLiveSubscription = (a, b) => {
  if (!a) {
    return b;
  }
  if (!b) {
    return a;
  }
  return subscriptionStatusRank(b) > subscriptionStatusRank(a) ? b : a;
};

export const addressValidationSchema = Yup.object().shape({
  address1: Yup.string().required("Required"),
  city: Yup.string().required("Required"),
  usState: Yup.string().required("Required"),
  zip: Yup.string().required("Required"),
});

export const getConsultationTypeByProduct = (product) => {
  switch (product) {
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
      return CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY;
    case ORDER_TYPE_PROP_1_PACK_REFILL:
    case ORDER_TYPE_PROP_2_PACK_REFILL:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP;
  }
};

export function anyOrderItemsAreMailOrder(orderItems) {
  return orderItems.some(
    (orderItem) => !isPharmacyPickupOrderType(orderItem.productSlug),
  );
}

export function anyOrderItemsArePharmacyPickup(orderItems) {
  return orderItems.some((orderItem) =>
    isPharmacyPickupOrderType(orderItem.productSlug),
  );
}

export function getAllMedicationsFromVisits(visits) {
  const medications = [];
  visits.forEach((visit) => {
    (visit.visitPrescriptions || []).forEach(
      ({ medicationName, medicationDosage }) => {
        medications.push({
          medicationName,
          medicationDosage,
        });
      },
    );
  });
  return medications;
}

/*
  Get medications to check if patient has ever been prescribed 20mg propranolol or atenolol.
  If doctor is prescribing 10mg propranolol and patient has received 20mg prop or atenolol in the past,
  then show a confirm.
*/
export function verifyNoteWithPrevMeds(note, meds) {
  const has20mgPropInPast = meds.some(
    ({ medicationName, medicationDosage }) =>
      medicationDosage === "20mg" &&
      medicationName.toLowerCase() === "propranolol",
  );
  const hasAtenololInPast = meds.some(
    ({ medicationName }) => medicationName.toLowerCase() === "atenolol",
  );

  if (
    note.includes("propranolol") &&
    !note.includes("20mg") &&
    has20mgPropInPast
  ) {
    if (
      !confirm(
        "CAUTION: Patient has been prescribed Propranolol 20mg in the past. Continue with 10mg prescription?",
      )
    ) {
      return false;
    }
  } else if (note.includes("propranolol") && hasAtenololInPast) {
    if (
      !confirm(
        "CAUTION: Patient has been prescribed Atenolol 25mg in the past. Continue with Propranolol prescription?",
      )
    ) {
      return false;
    }
  }

  return true;
}

export function getOptionsForEditOrderType(orderType) {
  // Handle empty orderType - default to sleep membership options for insomnia visits
  if (!orderType || orderType === "") {
    return [
      ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
      ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY,
      ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
      ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
    ];
  }

  // Sleep
  if (isSleepOrderType(orderType)) {
    if (orderType.includes("follow-up")) {
      return [
        ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP,
        ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP,
      ];
    } else {
      return [
        ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
        ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY,
        ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
        ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY,
      ];
    }
  }

  // Flavored BBs - now includes cross-category switching
  if (isFlavoredBBOrderType(orderType)) {
    if (orderType.includes("follow-up")) {
      return [
        ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
        ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
        ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
        ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
        // Removed ate-28 order types as they're now part of flavored PA
        ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
        ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP,
      ];
    } else {
      return [
        ORDER_TYPE_PROP_1_PACK,
        ORDER_TYPE_PROP_2_PACK,
        ORDER_TYPE_PROP_1_PACK_FLAVORED,
        ORDER_TYPE_PROP_2_PACK_FLAVORED,
        // Removed ate-28 order types as they're now part of flavored PA
        ORDER_TYPE_ATE_SCO_1_PACK,
        ORDER_TYPE_ATE_SCO_2_PACK,
      ];
    }
  }

  // BBs - now includes cross-category switching to Atenolol compounded
  if (isBBOrderType(orderType)) {
    if (orderType.includes("follow-up")) {
      return [
        ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
        ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
        ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
        ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
        ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
        ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP,
      ];
    } else {
      return [
        ORDER_TYPE_PROP_1_PACK,
        ORDER_TYPE_PROP_2_PACK,
        ORDER_TYPE_PROP_1_PACK_FLAVORED,
        ORDER_TYPE_PROP_2_PACK_FLAVORED,
        ORDER_TYPE_ATE_SCO_1_PACK,
        ORDER_TYPE_ATE_SCO_2_PACK,
      ];
    }
  }

  // Scopolamine Addon
  if (isScopolamineAddonOrderType(orderType)) {
    return [
      ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER,
      ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER,
    ];
  }

  // Atenolol Compounded - now includes cross-category switching to all PA order types
  if (isAteScoCompoundedOrderType(orderType)) {
    if (orderType.includes("follow-up")) {
      return [
        ORDER_TYPE_PROP_1_PACK_FOLLOW_UP,
        ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
        ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
        ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
        // Only combo order types remain for atenolol
        ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
        ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP,
      ];
    } else {
      return [
        ORDER_TYPE_PROP_1_PACK,
        ORDER_TYPE_PROP_2_PACK,
        ORDER_TYPE_PROP_1_PACK_FLAVORED,
        ORDER_TYPE_PROP_2_PACK_FLAVORED,
        // Only combo order types remain for atenolol
        ORDER_TYPE_ATE_SCO_1_PACK,
        ORDER_TYPE_ATE_SCO_2_PACK,
      ];
    }
  }

  // Return null instead of undefined for unknown order types
  return null;
}

export function isFollowUpConsultationType(consultationType) {
  return (
    consultationType ===
      CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP ||
    consultationType === CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP
  );
}

export function isFollowUpVisitType(visitType) {
  const followUpVisitTypes = [
    VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
    VISIT_TYPE_INSOMNIA_FOLLOW_UP,
    VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP,
  ];
  return followUpVisitTypes.includes(visitType);
}

export function isFollowUpOrderType(orderType) {
  return orderType.includes("follow-up");
}

export function displayOrderType(visitOrderType) {
  switch (visitOrderType) {
    case ORDER_TYPE_PROP_VISIT_ONLY:
      return "Performance Anxiety - Pickup";
    case ORDER_TYPE_PROP_1_PACK:
      return "Performance Anxiety - 1 Pack";
    case ORDER_TYPE_PROP_2_PACK:
      return "Performance Anxiety - 2 Pack";
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return "Performance Anxiety - Pickup (Follow Up)";
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return "Performance Anxiety - 1 Pack (Follow Up)";
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return "Performance Anxiety - 2 Pack (Follow Up)";

    // Flavored
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
      return "Performance Anxiety Flavored 🍒 - 1 Pack";
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
      return "Performance Anxiety Flavored 🍒 - 2 Pack";
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
      return "Performance Anxiety Flavored 🍒 - 1 Pack (Follow Up)";
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return "Performance Anxiety Flavored 🍒 - 2 Pack (Follow Up)";
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return "Performance Anxiety Flavored 🍒 - Sample Pack";
    case ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS:
      return "Performance Anxiety Flavored 🍒 - All Flavors Sample Pack";

    // Sleep
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY:
      return "Insomnia - Monthly";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_QUARTERLY:
      return "Insomnia - 3 Month Quarterly";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_SEMIANNUAL:
      return "Insomnia - 6 Month Semi-Annual";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_FOLLOW_UP:
      return "Insomnia - Follow Up";

    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY:
      return "Insomnia Pickup - Monthly";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_QUARTERLY:
      return "Insomnia Pickup - 3 Month Quarterly";
    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_SEMIANNUAL:
      return "Insomnia Pickup - 6 Month Semi-Annual";

    case ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_ONE_MONTH:
      return "Insomnia Pickup - 1 Month Trial";
    case ORDER_TYPE_SLEEP_RX_TRIAL_VISIT_ONLY_TWO_MONTHS:
      return "Insomnia Pickup - 2 Month Trial";
    case ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_ONE_MONTH:
      return "Insomnia - 1 Month Trial";
    case ORDER_TYPE_SLEEP_RX_TRIAL_MAIL_ORDER_TWO_MONTHS:
      return "Insomnia - 2 Month Trial";

    case ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP:
      return "Insomnia Pickup - Follow-Up";

    // Scopolamine Addon
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_MAIL_ORDER:
      return "Scopolamine Addon - 4 Pack (Mail Order)";
    case ORDER_TYPE_SCOPOLAMINE_ADDON_10_PACK_MAIL_ORDER:
      return "Scopolamine Addon - 10 Pack (Mail Order)";
    case ORDER_TYPE_SCOPOLAMINE_ADDON_4_PACK_VISIT_ONLY:
      return "Scopolamine Addon - 4 Pack (Local Pickup)";

    case ORDER_TYPE_ATE_SCO_1_PACK:
      return "Atenolol 28mg + Scopolamine 0.25mg - 1 Pack";
    case ORDER_TYPE_ATE_SCO_1_PACK_REFILL:
      return "Atenolol 28mg + Scopolamine 0.25mg - 1 Pack (Refill)";
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
      return "Atenolol 28mg + Scopolamine 0.25mg - 1 Pack (Follow Up)";
    case ORDER_TYPE_ATE_SCO_2_PACK:
      return "Atenolol 28mg + Scopolamine 0.25mg - 2 Pack";
    case ORDER_TYPE_ATE_SCO_2_PACK_REFILL:
      return "Atenolol 28mg + Scopolamine 0.25mg - 2 Pack (Refill)";
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return "Atenolol 28mg + Scopolamine 0.25mg - 2 Pack (Follow Up)";

    default:
      return `{UNKNOWN VISIT ORDER TYPE: ${visitOrderType}}`;
  }
}

export function displayOrderTypePacks(orderType, subcategory) {
  switch (orderType) {
    case ORDER_TYPE_PROP_1_PACK:
    case ORDER_TYPE_PROP_1_PACK_FOLLOW_UP:
      return "Beta Blockers 1 Pack";
    case ORDER_TYPE_PROP_2_PACK:
    case ORDER_TYPE_PROP_2_PACK_FOLLOW_UP:
      return "Beta Blockers 2 Pack";
    case ORDER_TYPE_PROP_VISIT_ONLY:
    case ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP:
      return "Beta Blockers Pickup (Visit Only)";
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
      return `Beta Blockers Flavored 1 Pack ${
        getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
      }`.trim();
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
      return `Beta Blockers Flavored 2 Pack ${
        getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
      }`.trim();
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return `Beta Blockers Flavored Sample Pack ${
        getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
      }`.trim();
    case ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS:
      return "Beta Blockers All Flavors Sample Pack";

    // Atenolol + Scopolamine Combo
    case ORDER_TYPE_ATE_SCO_1_PACK:
    case ORDER_TYPE_ATE_SCO_1_PACK_REFILL:
    case ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP:
      return `Atenolol + Scopolamine 1 Pack ${
        getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
      }`.trim();
    case ORDER_TYPE_ATE_SCO_2_PACK:
    case ORDER_TYPE_ATE_SCO_2_PACK_REFILL:
    case ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP:
      return `Atenolol + Scopolamine 2 Pack ${
        getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
      }`.trim();
  }
  return "";
}

export function generatePharmacyOrderLinkURL(pharmacyID, orderNumberOrFillID) {
  switch (pharmacyID) {
    case FILL_PHARMACY_ID_HONEYBEE_CA:
      return `https://partners.honeybeehealth.com/orders/${orderNumberOrFillID}`;
    default:
      return "";
  }
}

export function titleCase(str) {
  return str.replace("-", " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

const compoundedOrderTypes = [
  ORDER_TYPE_PROP_1_PACK_FLAVORED,
  ORDER_TYPE_PROP_2_PACK_FLAVORED,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
  ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS,
  // Atenolol + Scopolamine combo order types
  ORDER_TYPE_ATE_SCO_1_PACK,
  ORDER_TYPE_ATE_SCO_1_PACK_FOLLOW_UP,
  ORDER_TYPE_ATE_SCO_1_PACK_REFILL,
  ORDER_TYPE_ATE_SCO_2_PACK,
  ORDER_TYPE_ATE_SCO_2_PACK_FOLLOW_UP,
  ORDER_TYPE_ATE_SCO_2_PACK_REFILL,
];

const compoundedPropranololOrderTypes = [
  ORDER_TYPE_PROP_1_PACK_FLAVORED,
  ORDER_TYPE_PROP_2_PACK_FLAVORED,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
];

export function isCompoundedOrderType(orderType) {
  return compoundedOrderTypes.includes(orderType);
}

export function isCompoundedPropranololOrderType(orderType) {
  return compoundedPropranololOrderTypes.includes(orderType);
}

const scriptsureDrugNameMap = {
  PROPRANOLOL_10MG_FLAVORED: "Propranolol HCL 10mg",
  PROPRANOLOL_20MG_FLAVORED: "Propranolol HCL 20mg",
  PROPRANOLOL_12MG_FLAVORED: "Propranolol HCL 12mg",
  PROPRANOLOL_22MG_FLAVORED: "Propranolol HCL 22mg",
  ATENOLOL_28MG_FLAVORED: "Atenolol 28mg",
  DOXEPIN_3MG_GUMMY: "Doxepin 3mg",
  DOXEPIN_7MG_GUMMY: "Doxepin 7mg",
  ATENOLOL_28MG_SCOPOLAMINE_025MG_COMBO: "Atenolol 28mg + Scopolamine 0.25mg",
};

function getScriptsureDrugNamePrefix(productSlug) {
  if (productSlug.includes("1-pack")) {
    return "1 Pack";
  } else if (productSlug.includes("2-pack")) {
    return "2 Pack";
  } else if (productSlug.includes("trial")) {
    return "Sample Pack";
  }
  return "";
}

function createScriptsureDrugNameFromMedNameKey(medName) {
  const [medConstant, quantity] = medName.split("-");
  return `${scriptsureDrugNameMap[medConstant]} x${quantity}`;
}

export function getScriptsureDrugName(orderItem = {}, medName) {
  const { subcategory, productSlug = "" } = orderItem;

  if (!productSlug) {
    return createScriptsureDrugNameFromMedNameKey(medName);
  }

  // scriptsureDrugNamePrefix denotes the quantity (1 pack, 2 pack, or sample)
  const scriptsureDrugNamePrefix = getScriptsureDrugNamePrefix(productSlug);
  // scriptsureDrugName denotes the drug name and dosage
  const scriptsureDrugName = scriptsureDrugNameMap[medName];
  // subcategory denotes the flavor
  return `${scriptsureDrugNamePrefix} ${scriptsureDrugName} ${
    getFlavorName(subcategory) && `(${getFlavorName(subcategory)})`
  }`.trim();
}

export function getFlavorName(flavor = "") {
  switch (flavor) {
    case TANGERINE_FLAVOR:
      return FLAVOR_NAME_TANGERINE;
    case SPEARMINT_FLAVOR:
      return FLAVOR_NAME_SPEARMINT;
    case FRUIT_PUNCH_FLAVOR:
      return FLAVOR_NAME_FRUIT_PUNCH;
    case CHOCO_CHILL_MINT_FLAVOR:
      return FLAVOR_NAME_CHOCO_CHILL_MINT;
    case CITRUS_ZEN_FLAVOR:
      return FLAVOR_NAME_CITRUS_ZEN;
    default:
      return titleCase(flavor);
  }
}

export function isCompoundedMedication(medicationConstant) {
  return (
    medicationConstant.includes("FLAVORED") ||
    medicationConstant.endsWith("GUMMY")
  );
}

export function isQuarterlySubscription(orderType) {
  return orderType.includes("quarterly");
}

// Patient-facing label for a sleep membership/Rx order type, e.g.
// "Monthly Prescription Sleep Plan". Used on the portal's sleep treatment cards.
// Pass isFollowUp (from the visit type) for follow-up visits — their order type is still a
// recurring membership slug (monthly/quarterly), so the label can't be derived from it alone.
export function displaySleepPlanLabel(orderType, isFollowUp = false) {
  if (!orderType && !isFollowUp) return "Prescription Sleep Plan";

  let cadenceLabel;
  if (isFollowUp || orderType.includes("follow-up")) {
    // Follow-ups are labeled by their nature, not the underlying plan cadence.
    cadenceLabel = "Prescription Sleep Follow-up";
  } else if (orderType.includes("quarterly")) {
    cadenceLabel = "Quarterly Prescription Sleep Plan";
  } else if (orderType.includes("semiannual")) {
    cadenceLabel = "Semi-Annual Prescription Sleep Plan";
  } else if (orderType.includes("trial")) {
    cadenceLabel = orderType.includes("two-month")
      ? "2-Month Trial Prescription Sleep Plan"
      : "1-Month Trial Prescription Sleep Plan";
  } else if (orderType.includes("monthly")) {
    cadenceLabel = "Monthly Prescription Sleep Plan";
  } else {
    cadenceLabel = "Prescription Sleep Plan";
  }

  // Append the fulfillment so the plan name distinguishes mail-order from pharmacy pickup.
  const fulfillmentLabel = isPharmacyPickupOrderType(orderType)
    ? "Local Pharmacy Pickup"
    : "Mail Order";
  return `${cadenceLabel} (${fulfillmentLabel})`;
}

// Direct carrier tracking pages. The AfterShip branded page (aftership.gokick.com) is what we
// give patients; staff chasing a stuck shipment usually want the carrier's own page, which shows
// scan detail and exception codes AfterShip normalizes away.
const CARRIER_TRACKING_URLS = {
  usps: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`,
  ups: (n) => `https://www.ups.com/track?loc=en_US&tracknum=${n}`,
  fedex: (n) => `https://www.fedex.com/fedextrack/?trknbr=${n}`,
  dhl: (n) => `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${n}`,
};

const CARRIER_LABELS = {
  usps: "USPS",
  ups: "UPS",
  fedex: "FedEx",
  dhl: "DHL",
};

// detectShippingCarrier infers the carrier from a tracking number's format. We do NOT persist the
// carrier on pharmacy_fill_requests — it is only known transiently from the pharmacy's shipment
// payload — so the EMR recovers it from the number itself.
//
// Returns one of "usps" | "ups" | "fedex" | "dhl", or "" when the format is unrecognized or
// genuinely ambiguous. Callers must handle "" rather than guessing: sending staff to the wrong
// carrier's site is worse than sending them to the AfterShip page.
//
// Format rules (the unambiguous ones only):
//   UPS   1Z + 16 alphanumerics, or T + 10 digits
//   USPS  22-digit IMpb starting 92/93/94/95, 20-digit starting 0, or the 13-char
//         international form AA123456789US
//   FedEx 12 or 15 digits
//   DHL   10 digits
// Deliberately unhandled: 20-digit numbers starting 96 (FedEx SmartPost and USPS both use them)
// and bare 9-digit numbers.
export function detectShippingCarrier(trackingNumber) {
  const n = String(trackingNumber || "")
    .replace(/\s/g, "")
    .toUpperCase();
  if (!n) return "";

  if (/^1Z[0-9A-Z]{16}$/.test(n)) return "ups";
  if (/^T\d{10}$/.test(n)) return "ups";

  if (/^9[2-5]\d{20}$/.test(n)) return "usps";
  if (/^0\d{19}$/.test(n)) return "usps";
  if (/^[A-Z]{2}\d{9}US$/.test(n)) return "usps";

  if (/^\d{12}$/.test(n) || /^\d{15}$/.test(n)) return "fedex";

  if (/^\d{10}$/.test(n)) return "dhl";

  return "";
}

// normalizeCarrierName maps a carrier string as a pharmacy reported it onto our slug. Pharmacies
// are inconsistent ("FEDEX", "FedEx", "Federal Express", "United States Postal Service"), so match
// on a contains basis rather than equality.
function normalizeCarrierName(raw) {
  const s = String(raw || "").toLowerCase();
  if (!s) return "";
  if (s.includes("fedex") || s.includes("federal express")) return "fedex";
  if (s.includes("usps") || s.includes("postal")) return "usps";
  if (s.includes("dhl")) return "dhl";
  // Check UPS last and on a word boundary: "ups" is a substring of plenty of other words.
  if (/\bups\b/.test(s) || s === "ups") return "ups";
  return "";
}

// carrierFromShipmentPayload digs the carrier out of a pharmacy's raw shipment payload
// (pharmacy_fill_requests.raw_status / raw_response, exposed to the EMR as JSONRaw).
//
// The carrier is NOT a column on pharmacy_fill_requests — it is only ever read transiently from
// these payloads when we create the AfterShip tracking — and every pharmacy nests it somewhere
// different: HoneyBee puts it at medication_requests[0].medication_dispenses[n].shipment.carrier,
// others at the top level. Rather than hardcode each path, walk the object for a "carrier" key
// with a recognizable string value. Depth-limited so a pathological payload can't hang the UI.
export function carrierFromShipmentPayload(payload, depth = 0) {
  if (!payload || typeof payload !== "object" || depth > 6) return "";

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const found = carrierFromShipmentPayload(item, depth + 1);
      if (found) return found;
    }
    return "";
  }

  for (const [key, value] of Object.entries(payload)) {
    if (key.toLowerCase() === "carrier" && typeof value === "string") {
      const slug = normalizeCarrierName(value);
      if (slug) return slug;
    }
  }
  for (const value of Object.values(payload)) {
    const found = carrierFromShipmentPayload(value, depth + 1);
    if (found) return found;
  }
  return "";
}

// resolveShippingCarrier picks the carrier for a fill request row, preferring what the pharmacy
// actually told us over what the tracking number's format implies. Format detection is the
// fallback for rows whose payload never carried a carrier (e.g. Gogomeds, where we derived it
// from the shipment method instead).
export function resolveShippingCarrier(row) {
  if (!row) return "";
  return (
    carrierFromShipmentPayload(row.rawStatus) ||
    carrierFromShipmentPayload(row.rawResponse) ||
    detectShippingCarrier(row.trackingNumber)
  );
}

// carrierTrackingURL returns the carrier's own tracking page for a fill request row, or "" when
// the carrier can't be determined. Accepts a row object, or a bare tracking number when that's
// all the caller has.
export function carrierTrackingURL(rowOrTrackingNumber) {
  const row =
    typeof rowOrTrackingNumber === "string" || !rowOrTrackingNumber
      ? { trackingNumber: rowOrTrackingNumber }
      : rowOrTrackingNumber;
  const trackingNumber = String(row.trackingNumber || "").replace(/\s/g, "");
  const carrier = resolveShippingCarrier(row);
  if (!carrier || !trackingNumber) return "";
  return CARRIER_TRACKING_URLS[carrier](encodeURIComponent(trackingNumber));
}

// carrierDisplayName returns "USPS" / "FedEx" / etc. for a fill request row (or a bare tracking
// number), or "" if the carrier is unknown.
export function carrierDisplayName(rowOrTrackingNumber) {
  const row =
    typeof rowOrTrackingNumber === "string" || !rowOrTrackingNumber
      ? { trackingNumber: rowOrTrackingNumber }
      : rowOrTrackingNumber;
  return CARRIER_LABELS[resolveShippingCarrier(row)] || "";
}
